import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { MediaList, MediaUpload, usePhoto1Image } from 'helpers/ImageImports';
import { Product } from 'models/ProductModel';

const ProductForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.PRODUCT, Code.Form.Name.PRODUCT];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [segmentOptions, setSegmentOptions] = Hooks.Form.Options();
  const [categoryOptions, setCategoryOptions] = Hooks.Form.Options();
  const [typeOptions, setTypeOptions] = Hooks.Form.Options();
  const [unitOptions, setUnitOptions] = Hooks.Form.Options();
  const [taxOptions, setTaxOptions] = Hooks.Form.Options();
  const [offerOptions, setOfferOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Product>({});
  const [fileList, setFileList] = useState<MediaList>({});

  const [photo1Url, setPhoto1Url] = useState<string | null>(null);
  const {photo1Image, handlePhoto1ImageChange, handleAttachPhoto1Image, handleDeletePhoto1Image } = usePhoto1Image(fileList, setFileList, setPhoto1Url, setError);
   
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.SEGMENT, Code.Option.Common.SELECT, setSegmentOptions, setError);
    Files.Json.API(Code.File.CATEGORY, Code.Option.Common.SELECT, setCategoryOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.PRODUCT_TYPE, Code.Option.Common.NA, setTypeOptions, setError);
    Files.Json.API(Code.File.UNIT, Code.Option.Common.NA, setUnitOptions, setError);
    Files.Json.API(Code.File.TAX, Code.Option.Common.NA, setTaxOptions, setError);
    Files.Json.API(Code.File.OFFER, Code.Option.Common.NA, setOfferOptions, setError);
   
    API.Data.GetByKeyId<Product>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.product_active); 
        Files.List.ParseFile(data.file_list, setFileList);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
        }));
        setLoading(false);
      }})
      
      const onNewEntry = async () => {
        if(keyId===Code.Key.NEW) {
          setFormData((prev) => ({
            ...prev, 
            product_min_sale: 1,
            segment_id: Code.Option.Segment.OPTICAL
          }));
        }
        setLoading(false);
      };
      onNewEntry();

  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
      file_list: JSON.stringify(Files.List.Clean(fileList)),
    });

    API.Data.Save(formName, keyId, finalData, setMessage)
  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} formName={formName} formId={formId} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details", "More", "Images"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
           
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Select id={Name.Common.SegmentId} value={formData.segment_id} title={Title.Segment} options={segmentOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} selected={Code.Option.Segment.OPTICAL} />
                <Field.Select id={Name.Common.CategoryId} value={formData.category_id} title={Title.Category} options={categoryOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} />
              </Control.Row>
              <Control.Box>
                <Field.Text id={Name.Product.Name} value={formData.product_name} title={Title.Product} onChange={(e) => Form.Field.Change(e, formData, setFormData)} require={true} />
                <Field.Text id={Name.Product.Title} value={formData.product_title} title={Title.Particular} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Box>
              <Control.Row>
                <Field.Amount id={Name.Product.PurchasePrice} value={formData.product_purchase_price} title="Purchase Price" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Amount id={Name.Product.CostPrice} value={formData.product_cost_price} title="Cost Price" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Amount id={Name.Product.SalePrice} value={formData.product_sale_price} title="Sale Price" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Amount id={Name.Product.DepositPrice} value={formData.product_deposit_price} title="Deposit Price" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Product.Barcode} value={formData.product_barcode} title={Title.Barcode} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Text id={Name.Product.Model} value={formData.product_model_number} title={Title.Model} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Box>
                <Field.Area id={Name.Product.Comment} value={formData.product_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Box>
              <Control.Row>
                <Field.Amount id={Name.Product.MarginRate} value={formData.product_margin_rate} title="Margin Rate" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
              </Control.Row>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <Control.Row>
                <Field.Select id={Name.Common.TypeId} value={formData.type_id} title={Title.Type} options={typeOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Select id={Name.Common.UnitId} value={formData.unit_id} title={Title.Unit} options={unitOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Select id={Name.Tax.Id} value={formData.tax_id} title={Title.Tax} options={taxOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Select id={Name.Offer.Id} value={formData.offer_id} title={Title.Offer} options={offerOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Number id={Name.Product.MinSale} value={formData.product_min_sale} title="Min Sale Qty." onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Number id={Name.Product.MaxSale} value={formData.product_max_sale} title="Max Sale Qty." onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Number id={Name.Product.MinPurchase} value={formData.product_min_purchase} title="Min Purchase Qty." onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Number id={Name.Product.MaxPurchase} value={formData.product_max_purchase} title="Max Purchase Qty." onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Row>
                <Field.Number id={Name.Product.MinStock} value={formData.product_min_stock} title="Min Stock Qty." onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]}  />
                <Field.Number id={Name.Product.MaxStock} value={formData.product_max_stock} title="Max Stock Qty." onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab3} isActive={activeTab === Code.Tab.Tab3}>
              <MediaUpload label={Title.Phone} imageUrl={photo1Url} handleImageChange={handlePhoto1ImageChange} handleAttachImage={handleAttachPhoto1Image} handleDeleteImage={handleDeletePhoto1Image} summary={Info.ProductSize} />
            </Control.Tab.Content>
          
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default ProductForm;